import type { ICellRendererParams } from "ag-grid-community";
import * as React from "react";

import styles from "./ExpandableDetailPanel.module.css";

export interface ExpandableDetailPanelProps {
  children: React.ReactNode;
  /** AG Grid cell renderer params — used to dynamically size the row. */
  cellParams: ICellRendererParams<unknown>;
  /** DOM id so the chevron's `aria-controls` can resolve to this panel. */
  id?: string;
}

/**
 * Wrapper for the consumer's expanded row content. Owns the `ResizeObserver`
 * that grows the AG Grid row to fit the rendered body.
 */
export function ExpandableDetailPanel({
  children,
  cellParams,
  id,
}: ExpandableDetailPanelProps) {
  const ref = React.useRef<HTMLDivElement>(null);
  const { node, api } = cellParams;

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    const update = () => {
      node.setRowHeight(el.scrollHeight);
      api.onRowHeightChanged();
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [node, api]);

  return (
    <div ref={ref} id={id} className={styles.detailPanel} role="region">
      {children}
    </div>
  );
}
